<script setup lang="ts">
import type { SearchResponse } from '@algolia/client-search';
import { useMounted, whenever } from '@vueuse/core';
import { connectStats } from 'instantsearch.js/es/connectors';
import {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import { computed, onMounted, ref } from 'vue';

import { AlgoliaCategory, searchCategories } from '@/api/algolia';
import FeaturedCategories from '@/components/base/FeaturedCategories.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useWidget } from '@/composables/instant-search/useWidget';
import { useChat } from '@/stores/chat';

const chat = useChat();

defineEmits<{ 'select-search-term': [string] }>();

const featuredCategories = ref<SearchResponse<AlgoliaCategory>>();

const { state } = useWidget<StatsWidgetDescription, StatsConnectorParams>(
  { connector: connectStats },
  computed(() => ({})),
);

const loadCategories = async () => {
  featuredCategories.value = await searchCategories({
    distinct: true,
    hitsPerPage: 5,
    ruleContexts: ['noResults'],
  });
};

onMounted(chat.init);

const mounted = useMounted();
whenever(() => mounted.value && state.value?.nbHits === 0, loadCategories);
</script>

<template>
  <div v-if="!state?.nbHits" class="no-search-results" data-test="no-search-results">
    <div class="flex flex-col items-center w-full">
      <Header3 class="font-proxima-nova">
        Nuts! We found no matches for {{ `'${state?.query || 'this query'}'` }}!
      </Header3>
      <SmallBodyText class="py-4 text-gray-500">
        Need help?
        <template v-if="chat.loaded">
          Contact us on
          <UnstyledButton v-if="chat.available" data-test="no-results-chat-text" @click="chat.open">
            <SmallBodyText class="font-semibold text-black" underline>live chat</SmallBodyText>
          </UnstyledButton>
          <a
            v-else
            href="tel:+1-800-558-6887"
            class="inline-block hover:no-underline"
            data-test="no-results-telephone"
          >
            <SmallBodyText
              class="font-semibold text-black"
              data-test="telephone"
              itemprop="telephone"
            >
              800-558-6887
            </SmallBodyText>
          </a>
          or try one of these popular help pages.
        </template>
        <template v-else>Try one of these popular help pages.</template>
      </SmallBodyText>
      <ul class="inline-flex gap-x-6">
        <li>
          <a href="/help">
            <SmallBodyText class="my-0 font-bold text-nuts-cyan-600 hover:underline">
              Help Center
            </SmallBodyText>
          </a>
        </li>
        <li>
          <a href="https://help.nuts.com/en_us/how-do-i-make-changes-to-my-auto-delivery-SJYPZKARE">
            <SmallBodyText class="font-bold text-nuts-cyan-600 hover:underline">
              Cancel Auto-Delivery
            </SmallBodyText>
          </a>
        </li>
      </ul>
      <transition name="fade">
        <FeaturedCategories
          v-if="featuredCategories?.hits.length"
          class="gap-4 full-width lg:w-auto mt-14"
          :categories="featuredCategories.hits"
          @click="(category) => $emit('select-search-term', category)"
        >
          <template #title>
            <Header4 class="flex font-sofia-pro">Popular Search Terms</Header4>
          </template>
        </FeaturedCategories>
      </transition>
    </div>
  </div>
</template>
